import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../Auth/Middleware";
import StudentReportCard from "./StudentReportCard";
import { formatDate } from "../../utils";
import logoSrc from "../../assets/ooklass-blue-removebg.png";

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 12;
const USABLE_W = PAGE_W - MARGIN * 2;
const BLUE = [21, 101, 192];
const GREY = [100, 100, 100];

// Loads the logo PNG and returns { dataUrl, aspect }
function loadLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: c.toDataURL("image/png"), aspect: img.width / img.height });
    };
    img.src = logoSrc;
  });
}

export default function StudentReportModal({ open, onClose, studentId, batch }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    if (!open || !studentId) return;
    setReport(null);
    setError(null);
    setRemarks("");
    setLoading(true);
    api
      .get(`Student/StudentReport/${studentId}?batch=${batch}`)
      .then((res) => { setReport(res.data); setLoading(false); })
      .catch(() => { setError("Failed to load report. Please try again."); setLoading(false); });
  }, [open, studentId, batch]);

  const handleDownload = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      let y = MARGIN;

      // ── helpers ──────────────────────────────────────────────────────────────

      const needSpace = (h) => {
        if (y + h > PAGE_H - MARGIN) { pdf.addPage(); y = MARGIN; }
      };

      const sectionTitle = (text) => {
        needSpace(10);
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...BLUE);
        pdf.text(text.toUpperCase(), MARGIN, y);
        y += 1;
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
        y += 4;
        pdf.setTextColor(0, 0, 0);
      };


      // ── 1. Header ─────────────────────────────────────────────────────────────

      const { dataUrl: logoData, aspect } = await loadLogo();
      const logoH = 14;
      const logoW = logoH * aspect;
      pdf.addImage(logoData, "PNG", MARGIN, y, logoW, logoH);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...BLUE);
      pdf.text(`Batch ${report.batchYear}`, PAGE_W - MARGIN, y + logoH / 2, { align: "right" });
      y += logoH + 3;

      pdf.setDrawColor(...BLUE);
      pdf.setLineWidth(0.5);
      pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
      y += 5;

      // ── 2. Student info (two-column) ──────────────────────────────────────────

      pdf.setLineWidth(0.3);
      const col2 = PAGE_W / 2 + 2;
      const rowH = 5.5;

      const infoL = [
        ["Student", report.name],
        ["Father", report.fatherName],
        ["DOB", formatDate(new Date(report.dateOfBirth))],
        ["Status", report.enrollmentStatus],
      ];
      const infoR = [
        ["Class", `${report.class}th`],
        ["School", report.school || "\u2014"],
        ["Contact", report.parentContact || "\u2014"],
      ];

      const infoRows = Math.max(infoL.length, infoR.length);
      infoL.forEach(([label, val], i) => {
        pdf.setFontSize(8.5);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...GREY);
        pdf.text(`${label}:`, MARGIN, y + i * rowH);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(30, 30, 30);
        pdf.text(val, MARGIN + 20, y + i * rowH);
      });
      infoR.forEach(([label, val], i) => {
        pdf.setFontSize(8.5);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...GREY);
        pdf.text(`${label}:`, col2, y + i * rowH);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(30, 30, 30);
        pdf.text(val, col2 + 18, y + i * rowH);
      });
      y += infoRows * rowH + 5;

      // ── 3. Performance summary table ──────────────────────────────────────────

      sectionTitle("Performance Summary");

      autoTable(pdf, {
        startY: y,
        head: [["Subject", "Tests", "Avg %", "Highest", "Lowest", "Absent"]],
        body: report.subjects.map((s) => {
          const absentCount = s.tests.filter((t) => t.marks === -1 || t.marks == null).length;
          return [
            s.courseName,
            s.testCount,
            `${s.average.toFixed(1)}%`,
            s.highest,
            s.lowest,
            absentCount || "\u2014",
          ];
        }),
        headStyles: { fillColor: BLUE, fontSize: 8.5, fontStyle: "bold" },
        bodyStyles: { fontSize: 8.5 },
        margin: { left: MARGIN, right: MARGIN },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 2) {
            const val = parseFloat(data.cell.raw);
            if (!isNaN(val)) {
              data.cell.styles.textColor = val >= 75 ? [46, 125, 50] : val >= 50 ? [230, 81, 0] : [198, 40, 40];
              data.cell.styles.fontStyle = "bold";
            }
          }
          if (data.section === "body" && data.column.index === 5 && data.cell.raw !== "\u2014") {
            data.cell.styles.textColor = [198, 40, 40];
          }
        },
      });
      y = pdf.lastAutoTable.finalY + 6;

      // ── 5. Per-subject detail ─────────────────────────────────────────────────

      for (const subject of report.subjects) {
        sectionTitle(subject.courseName);

        // Chart: read directly from the Chart.js canvas element (no html2canvas needed)
        const subjectSection = document.querySelector(
          `[data-pdf-section="subject-${subject.courseId}"]`
        );
        const chartCanvas = subjectSection?.querySelector("canvas");
        if (chartCanvas && chartCanvas.width > 0) {
          const chartData = chartCanvas.toDataURL("image/png");
          const chartH = Math.min(55, (chartCanvas.height * USABLE_W) / chartCanvas.width);
          const chartW = (chartCanvas.width / chartCanvas.height) * chartH;
          needSpace(chartH + 4);
          pdf.addImage(chartData, "PNG", MARGIN, y, Math.min(chartW, USABLE_W), chartH);
          y += chartH + 4;
        }

        // Test detail table — autotable handles page breaks natively
        const sortedTests = [...subject.tests].sort(
          (a, b) => new Date(b.dateOfExam) - new Date(a.dateOfExam)
        );

        autoTable(pdf, {
          startY: y,
          head: [["Date", "Marks", "Max Marks", "Score %", "Rank"]],
          body: sortedTests.map((test) => {
            const absent = test.marks === -1 || test.marks == null;
            const pct = absent ? "\u2014" : `${((test.marks * 100) / test.maxMarks).toFixed(1)}%`;
            const rank =
              !absent && test.rankInClass && test.totalStudentsPresent
                ? `${test.rankInClass} / ${test.totalStudentsPresent}`
                : "\u2014";
            return [
              formatDate(new Date(test.dateOfExam)),
              absent ? "Absent" : test.marks,
              absent ? "\u2014" : test.maxMarks,
              pct,
              rank,
            ];
          }),
          headStyles: { fillColor: [245, 245, 245], textColor: [30, 30, 30], fontStyle: "bold", fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          margin: { left: MARGIN, right: MARGIN },
          didParseCell: (data) => {
            if (data.section === "body" && data.cell.raw === "Absent") {
              data.cell.styles.textColor = [198, 40, 40];
              data.cell.styles.fontStyle = "italic";
            }
            if (data.section === "body" && data.column.index === 3) {
              const val = parseFloat(data.cell.raw);
              if (!isNaN(val)) {
                data.cell.styles.textColor = val >= 75 ? [46, 125, 50] : val >= 50 ? [230, 81, 0] : [198, 40, 40];
              }
            }
          },
        });
        y = pdf.lastAutoTable.finalY + 3;

        // Absent badges as inline text
        const absentTests = sortedTests.filter((t) => t.marks === -1 || t.marks == null);
        if (absentTests.length > 0) {
          needSpace(6);
          pdf.setFontSize(7.5);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(198, 40, 40);
          pdf.text(
            "Absent: " + absentTests.map((t) => formatDate(new Date(t.dateOfExam))).join("  ·  "),
            MARGIN,
            y
          );
          y += 5;
        }

        y += 4;
      }

      // ── 6. Remarks ────────────────────────────────────────────────────────────

      needSpace(32);
      sectionTitle("Remarks");
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(MARGIN, y, USABLE_W, 24, 1.5, 1.5);
      if (remarks.trim()) {
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(30, 30, 30);
        pdf.text(remarks.trim(), MARGIN + 3, y + 5, { maxWidth: USABLE_W - 6 });
      }

      // ── Save ──────────────────────────────────────────────────────────────────

      const safeName = report.name.replace(/\s+/g, "");
      pdf.save(`Report_${safeName}_${report.batchYear}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Student Performance Report
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
            <CircularProgress />
          </div>
        )}
        {error && (
          <Typography color="error" sx={{ p: 3 }}>
            {error}
          </Typography>
        )}
        {report && (
          <>
            <div style={{ overflowX: "auto", background: "#f5f5f5", padding: "16px" }}>
              <StudentReportCard ref={reportRef} report={report} remarks={remarks} />
            </div>
            <div style={{ padding: "16px" }}>
              <TextField
                label="Remarks (optional)"
                multiline
                rows={3}
                fullWidth
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any remarks for the student..."
              />
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          disabled={!report || downloading}
        >
          {downloading ? "Generating PDF…" : "Download PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
